import { useCallback, useMemo, useState } from 'react'

import { CursorState } from '@editor/config/constants/cursor'
import { usePrintScale } from '@editor/hooks/usePrintScale'
import { PointArray } from '@editor/interfaces/attribute'
import { LineStyle, PolylineProps } from '@editor/interfaces/canvas'
import { DrawingParameterPolyline, DrawingType } from '@editor/interfaces/drawing'
import { CanvasConfigV2, CanvasEventsExtra } from '@editor/interfaces/editor'
import { BlueprintEditorTools, DEFAULT_STYLES, Mode } from '@editor/pages/projects/blueprint-editor/definitions'
import usePointSelection from '@editor/pages/projects/blueprint-editor/hooks/usePointSelection'
import useProjectionPlane from '@editor/pages/projects/blueprint-editor/hooks/useProjectionPlane'
import { addDrawing, updateDrawingParameters } from '@editor/pages/projects/blueprint-editor/store/drawings'
import { getDrawingsOfType } from '@editor/services/Drawing'
import { convertPrintDimension } from '@editor/services/Util'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { useToolUtilSelector } from '@editor/store/selectors'
import { setCursor, setWorkingElementId, toggleSelectedElementIds } from '@editor/store/tool'
import { CircleAnchorIcon } from '@infraloop/common'
import { ThreeEvent } from '@react-three/fiber'
import { Vector3 } from 'three'

import { getLastCount, getNewOrder } from '../../utils'
import { WORKING_ID } from '../constants'
import { addWorkingPoint, reset } from '../store'
import useUndo from './useUndo'

const DISTANCE_LABEL_STYLES: Partial<PolylineProps> = {
  anchorProps: {
    color: '#f00',
    icon: undefined,
    outlineOpacity: 1,
    scale: 1,
  },
  line: {
    style: LineStyle.Solid,
    color: DEFAULT_STYLES.strokeColor,
    thickness: 0.004,
  },
  hideAnchors: true,
}

const useMainCanvas = (): CanvasConfigV2 => {
  // Hooks
  const { projectionPlane } = useProjectionPlane()
  const { handleUndo } = useUndo()
  const { currentPrintScale } = usePrintScale()
  const { getIntersection } = usePointSelection()

  // Store
  const dispatch = useAppDispatch()
  const project = useAppSelector((state) => state.page.project)
  const inspectionArea = useAppSelector((state) => state.page.inspectionArea)

  const hiddenElementIds: string[] = useToolUtilSelector((state) => state.hiddenElementIds)
  const editingElementId = useToolUtilSelector((state) => state.editingElementId)

  const selectedTool = useAppSelector((state) => state.blueprint.editor.selectedTool)
  const selectedBlueprint = useAppSelector((state) => state.blueprint.editor.selectedBlueprint)
  const mode = useAppSelector((state) => state.blueprint.editor.mode)
  const drawings = useAppSelector((state) => state.blueprint.drawings.document.drawings)
  const defaultStyles = useAppSelector((state) => state.blueprint.drawings.defaultStyles)
  const renderSkipUuids = useAppSelector((state) => state.blueprint.drawings.renderSkipUuids)
  const isAllowedToModify = useAppSelector((state) => state.blueprint.editor.isAllowedToModify)

  const workingPoints = useAppSelector((state) => state.bpToolPen.workingPoints)

  // State
  const [isClosing, setIsClosing] = useState(false)

  // Derived state
  const isToolSelected = selectedTool === BlueprintEditorTools.Pen

  /**
   * End drawing of polyline
   */
  const endPolyline = useCallback(
    (point?: Vector3) => {
      if (workingPoints.length < 2 || !project || !inspectionArea || !selectedBlueprint?.sort_key) {
        setIsClosing(false)
        return
      }

      const points = workingPoints.concat(point ? [point.toArray()] : []).map((p) => p)

      dispatch(
        addDrawing({
          name: `ポリライン ${
            getLastCount(
              drawings.map((p) => p.name),
              'ポリライン',
            ) + 1
          }`,
          drawing_type: DrawingType.Polyline,
          drawing_parameters: {
            uuid: crypto.randomUUID(),
            points,
            transformation: [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1],
            order: getNewOrder(drawings),
            styles: defaultStyles,
          },
        }),
      )

      setIsClosing(false)
      dispatch(reset())
      dispatch(setWorkingElementId(undefined))
    },
    [project, inspectionArea, selectedBlueprint, drawings, workingPoints, defaultStyles, dispatch],
  )

  /**
   * Main action to draw the polyline.
   *
   * @param points Clicked point in 3D Space.
   * @param extra Extra events data.
   */
  const onMouseUp = useCallback(
    (point: PointArray | undefined, { isDragged, isDoubleClicked }: CanvasEventsExtra) => {
      if (isClosing) endPolyline()
      if (!point || isDragged || !isToolSelected || isClosing || editingElementId) return

      // Double click to end the polyline
      if (isDoubleClicked && workingPoints.length >= 2) {
        endPolyline()
        return
      }

      dispatch(addWorkingPoint(point))
      dispatch(setWorkingElementId(WORKING_ID))
    },
    [isToolSelected, workingPoints, editingElementId, isClosing, endPolyline, dispatch],
  )

  /**
   * Handle undo action.
   */
  const onUndo = useCallback(() => {
    if (!isToolSelected) return

    handleUndo()
  }, [isToolSelected, handleUndo])

  return {
    objects: {
      circleAnchors: [
        /**
         * THE anchor to click to close the polyline.
         */
        ...useMemo(
          () => [
            // Ending anchor
            ...(workingPoints.length > 1
              ? [
                  {
                    id: 'working-polyline-complete-anchor',
                    point: workingPoints[workingPoints.length - 1] || [0, 0, 0],
                    outlineOpacity: 0,
                    color: isClosing ? '#f00' : '#248CE4',
                    scale: 1.3,
                    icon: CircleAnchorIcon.CHECK,
                    iconScale: 1.1,
                    iconColor: 'white',
                    tooltipText: isClosing ? 'このポリラインの作成を完了' : undefined,
                    depthTest: false,
                    depthWrite: false,
                    onEnter: (event: ThreeEvent<PointerEvent>) => {
                      event.stopPropagation()
                      dispatch(setCursor(CursorState.POINTER))
                      setIsClosing(true)
                    },
                    onLeave: (event: ThreeEvent<PointerEvent>) => {
                      event.stopPropagation()
                      dispatch(setCursor(CursorState.CROSSHAIR))
                      setIsClosing(false)
                    },
                  },
                ]
              : []),
          ],
          [isClosing, workingPoints, dispatch],
        ),
      ],

      polylines: [
        /**
         * Working polyline.
         */
        ...useMemo((): PolylineProps[] => {
          // Don't create a label if there are no points
          // Re-using might be best for optimization, but painful to reset between new drawings.
          if (!workingPoints.length) return []

          const style = DISTANCE_LABEL_STYLES
          if (isClosing) {
            style.topAnchorProps = { ...style.topAnchorProps, color: '#f00' }
          }

          return [
            {
              ...style,
              id: 'working-polyline-drawing-outline',
              points: workingPoints,
              hideAnchors: true,
              isWorking: true,
              visible: workingPoints.length >= 1,
              line: {
                style: defaultStyles?.strokeStyle || DEFAULT_STYLES.strokeStyle,
                color: defaultStyles?.strokeColor,
                thickness: convertPrintDimension(defaultStyles?.strokeWidth || 1, currentPrintScale),
              },
            },
          ]
        }, [isClosing, workingPoints, defaultStyles, currentPrintScale]),

        /**
         * Saved polyline.
         */
        ...useMemo(
          () =>
            getDrawingsOfType<DrawingParameterPolyline>(
              drawings,
              DrawingType.Polyline,
              renderSkipUuids,
            ).map<PolylineProps>(({ drawing_parameters: { uuid, points, styles } }) => {
              let finalPoints = [...points]
              if ([Mode.Editor2D].includes(mode) && projectionPlane) {
                finalPoints = points.map((p) =>
                  projectionPlane.projectPoint(new Vector3(...p), new Vector3()).toArray(),
                )
              }

              const isEditing = editingElementId === uuid

              return {
                ...DISTANCE_LABEL_STYLES,
                id: uuid,
                points: finalPoints,
                visible: !hiddenElementIds.includes(uuid) && mode !== Mode.Editor3D,
                isEditing,
                hideAnchors: true,
                vertexAnchors: isAllowedToModify
                  ? {
                      showOnHover: true,
                      onUpdated: (pts) => {
                        dispatch(
                          updateDrawingParameters({
                            uuid: editingElementId,
                            points: pts,
                          }),
                        )
                        dispatch(toggleSelectedElementIds({ ids: [uuid], selected: true }))
                      },
                    }
                  : undefined,
                line: {
                  style: styles?.strokeStyle || DEFAULT_STYLES.strokeStyle,
                  color: styles?.strokeColor,
                  thickness: convertPrintDimension(styles?.strokeWidth || 1, currentPrintScale),
                },
              }
            }),
          [
            drawings,
            renderSkipUuids,
            hiddenElementIds,
            editingElementId,
            mode,
            projectionPlane,
            currentPrintScale,
            isAllowedToModify,
            dispatch,
          ],
        ),
      ],
    },
    events: {
      onMouseUp(e, extra) {
        if (!isToolSelected) {
          return
        }

        const point = getIntersection(e)
        onMouseUp(point, extra)
      },

      /**
       * Undo the last point of the working polyline.
       * If the last polyline is completed, it will remove the last point and make it the drawing stage.
       */
      onUndo,

      onCancel: useCallback(() => {
        if (!isToolSelected) return

        dispatch(reset())
        dispatch(setWorkingElementId(undefined))
      }, [isToolSelected, dispatch]),
    },
  }
}

export default useMainCanvas
